import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormValues, change } from 'redux-form';
import TagManager from 'react-gtm-module';
import { withStyles } from '@material-ui/core/styles';
import flatpickr from 'flatpickr';
// import 'flatpickr/dist/flatpickr.css';
import { constructGTM } from '../../../../utilities/helpers';
import styles from './styles';
import Button from '../../../Button';
import formFields from './formFields';
import TextField from '../../../../fields/reduxForm/GeneralTextField';
import {
  FORMS,
  USER_ROLE_TYPES_LIST,
  USER_ROLE_TYPES,
} from '../../../../constants';
import {
  validateEmailUnique,
  maxLength9,
  maxLength10,
  required,
} from '../../../../utilities/validators';
import { resetRegistrationInfo } from '../../../../store/actions/registration';

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  classes: PropTypes.shape({
    actionsRow: PropTypes.string,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onResetRegistrationInfo: PropTypes.func.isRequired,
  changeFieldValue: PropTypes.func, // from redux-form
};

const defaultProps = {
  formValues: {},
};

// We only use this validate function to validate the legal checkbox field when the form is submitted.
// The validation for other fields are handled by field-level validation when the field is blurred.
const validate = values => {
  const errors = [];
  if (!values.legal)
    errors.legal =
      'You must agree to our Terms of Use and Privacy Policy before proceeding.';

  if (!values['custom:roleType']) {
    errors['custom:roleType'] = 'Required';
  }
  return errors;
};

const asyncValidate = values =>
  validateEmailUnique('custom:personalEmail', values);

const onSubmitFail = errors => {
  // Log failed event to GTM
  TagManager.initialize(
    constructGTM({
      dataLayer: {
        event: 'Registration step 1/3 - error',
        errorsReg1: errors,
      },
    }),
  );
};

export const RegistrationForm = props => {
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [validationProps, setValidationProps] = useState([]);
  const {
    handleSubmit,
    classes,
    onCancel,
    onResetRegistrationInfo,
    formValues,
    changeFieldValue,
  } = props;

  const renderButton = () => (
    <div className={classes.actionsRow}>
      <div className={classes.closeButton}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleCancel}
          style={{ height: '61px' }}
        >
          Close
        </Button>
      </div>
      <div className={classes.nextButton}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          style={{ color: 'white', height: '61px' }}
        >
          Next
        </Button>
      </div>
    </div>
  );

  // Toggle availability of calendar display
  useEffect(() => {
    if (calendarVisible) {
      const dateInput = document.querySelector(
        '#building-licence-expiry-container input',
      );
      renderFlatpickr(dateInput);
    }
  }, [calendarVisible]);

  // Updates form value of expiry w/ full date object
  useEffect(() => {
    if (expiryDate) {
      const maskInput = document.querySelector(
        '#building-licence-expiry-mask-field',
      );
      changeFieldValue('custom:buildingLicExp', expiryDate);

      const format = moment(expiryDate).format('DD-MM-YYYY');
      maskInput.value = `${format}`;
    }
  }, [expiryDate]);

  // Render datepicker element
  const renderFlatpickr = dateInput => {
    const options = {
      dateFormat: 'd-m-Y',
      minDate: 'today',
      disableMobile: true,
      static: true,
      onChange: () => {
        handleDateChange(datepicker);
      },
    };
    const datepicker = flatpickr(dateInput, options);
    datepicker.calendarContainer.style.fontFamily = '"Helvetica Neue", Arial';
  };

  const handleDateChange = datepicker => {
    const maskInput = document.querySelector(
      '#building-licence-expiry-mask-field',
    );
    if (maskInput) maskInput.blur();
    const [selectedDate] = datepicker.selectedDates;
    setValidationProps([required, maxLength10]);
    setExpiryDate(selectedDate.toISOString());
    if (maskInput) maskInput.blur();
  };

  const handleCancel = () => {
    onCancel();
    onResetRegistrationInfo();
  };

  const handleRoleTypeChange = () => {
    // everytime the Role Type change, clear the builder license fields & flip calendar state.
    const roleSelect = document.querySelector('#role-type-select-field').value;
    if (roleSelect === 'B') {
      setCalendarVisible(true);
    } else {
      setCalendarVisible(false);
    }
    changeFieldValue('custom:buildingLicNo', null);
    changeFieldValue('custom:buildingLicExp', null);
    changeFieldValue('custom:buildingLicExpMask', null);
  };

  const getRoleOptions = () => {
    const options = USER_ROLE_TYPES_LIST.map(roleItem => (
      <option key={roleItem.code} value={roleItem.code}>
        {roleItem.displayLabel}
      </option>
    ));
    return options;
  };

  return (
    <form onSubmit={handleSubmit}>
      {formFields.map(field => (
        <div className={classes.formGroup} key={field.name}>
          {field.name === 'custom:roleType' ? (
            <>
              <div className={classes.roleSelectContain}>
                <Field
                  classes={classes}
                  name={field.name}
                  component={field.component}
                  label="Role Type"
                  validate={field.validate}
                  props={{
                    name: field.name,
                    id: 'role-type-select-field',
                    label: 'Select Role Type',
                    required: true,
                    onSelectChange: handleRoleTypeChange,
                  }}
                >
                  <option />
                  {getRoleOptions()}
                </Field>
              </div>
              {formValues['custom:roleType'] ===
                USER_ROLE_TYPES.BUILDER.code && (
                <>
                  <div className={classes.formGroup}>
                    <Field
                      name="custom:buildingLicNo"
                      component={TextField}
                      props={{
                        id: 'building-licence-number-text-field',
                        label: 'Builder Licence Number',
                        fullWidth: true,
                        required: true,
                      }}
                      validate={[required, maxLength9]}
                    />
                  </div>
                  <div
                    className={classes.formGroup}
                    id="building-licence-expiry-container"
                  >
                    <Field
                      name="custom:buildingLicExpMask"
                      component={TextField}
                      autoComplete="off"
                      props={{
                        id: 'building-licence-expiry-mask-field',
                        label: 'Builder Licence Expiry',
                        fullWidth: true,
                      }}
                      validate={validationProps}
                    />
                  </div>
                  <div
                    className={classes.hiddenInput}
                    id="building-licence-expiry-container"
                  >
                    <Field
                      name="custom:buildingLicExp"
                      component={TextField}
                      props={{
                        id: 'building-licence-expiry-text-field',
                        label: 'Builder Licence Expiry Value',
                        fullWidth: true,
                        required: true,
                      }}
                      validate={[required]}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <Field
              name={field.name}
              component={field.component}
              props={field.props}
              validate={field.validate}
            />
          )}
        </div>
      ))}
      <div className={classes.actionsRow}>{renderButton()}</div>
    </form>
  );
};

RegistrationForm.propTypes = propTypes;
RegistrationForm.defaultProps = defaultProps;

const mapDispatchToProps = dispatch => ({
  onResetRegistrationInfo: () => dispatch(resetRegistrationInfo()),
  changeFieldValue: (field, value) =>
    dispatch(change(FORMS.REGISTRATION_SHORT, field, value)),
});

const mapStateToProps = state => ({
  formValues: getFormValues(FORMS.REGISTRATION_SHORT)(state),
});

export default reduxForm({
  form: FORMS.REGISTRATION_SHORT,
  validate,
  asyncValidate,
  asyncBlurFields: ['custom:personalEmail'],
  onSubmitFail,
})(
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(RegistrationForm),
  ),
);
